import { FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import { Fragment } from 'react';
import type { FieldComponentProps } from '../form-engine/types';

// Reference year used for day/month-only dates (yearly-recurring events).
// Non-leap year so 29/02 stays disabled and the value never drifts across years.
const DAY_MONTH_REFERENCE_YEAR = 1900;

const DateField = ({ field }: FieldComponentProps) => {
  const { getFormDataItem, handleChange } = useFormContext();
  const formData = useFormData();

  const format = field.format || 'DD/MM';
  const isDayMonthOnly = !format.includes('YYYY');

  const rawValue = getFormDataItem<string | undefined>(formData, field.name!);
  const value = rawValue ? (isDayMonthOnly ? dayjs(rawValue).year(DAY_MONTH_REFERENCE_YEAR) : dayjs(rawValue)) : null;

  const label = Object.prototype.hasOwnProperty.call(field, 'help') ? (
    <Fragment>
      {field.label}
      <ChartHelpTooltip chartType={field.help!} type="label" />
    </Fragment>
  ) : (
    field.label
  );

  return (
    <Grid size={field.size || 12} key={field.name}>
      <FormControl required={field.required || false} sx={{ width: '100%', pt: 0 }}>
        <DatePicker
          label={label}
          name={field.name}
          value={value}
          format={format}
          views={isDayMonthOnly ? ['month', 'day'] : ['year', 'month', 'day']}
          openTo={isDayMonthOnly ? 'month' : 'year'}
          minDate={isDayMonthOnly ? dayjs(`${DAY_MONTH_REFERENCE_YEAR}-01-01`) : undefined}
          maxDate={isDayMonthOnly ? dayjs(`${DAY_MONTH_REFERENCE_YEAR}-12-31`) : undefined}
          onChange={(e) => {
            handleChange(
              {
                target: {
                  name: field.name!,
                  value: dayjs(e).format('YYYY-MM-DD'),
                },
              },
              field,
            );
          }}
          disabled={Object.prototype.hasOwnProperty.call(field, 'disabled') ? getFormDataItem(formData, field.disabled!) : false}
          shouldDisableDate={(date) => {
            // Disable the 29/2 if the year is not a leap year
            return date.date() === 29 && date.month() === 1;
          }}
        />
      </FormControl>
    </Grid>
  );
};

export default DateField;
